import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { authClient } from '@/lib/auth-client';

export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const { data: session, isPending } = authClient.useSession();

  // Close the mobile menu on navigation, or it hangs over the new page.
  useEffect(() => setMenuOpen(false), [pathname]);

  // Admin check: the applications endpoint answers 403 for non-admins, so its
  // status code doubles as the role probe. Cached to avoid re-asking constantly.
  const { data: adminCheck } = useQuery({
    queryKey: ['is-admin', session?.user?.id],
    queryFn: async () => {
      const res = await fetch('/api/admin/applications?type=writer&status=pending');
      return { isAdmin: res.status !== 403 };
    },
    enabled: !!session?.user,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const isAdmin = adminCheck?.isAdmin === true;

  if (pathname.startsWith('/account/')) return null;

  const linkClass = 'text-sm text-gray-300 hover:text-white transition-colors duration-150';

  return (
    <nav
      style={{ backgroundColor: '#1E3A2E' }}
      className="sticky top-0 z-50 border-b border-[#2D5040]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-9 h-9 rounded overflow-hidden bg-black flex-shrink-0">
              <img
                src="https://dtvoeevhaseb5.cloudfront.net/user-uploads/8d980eb5-4562-4e34-9e16-f6438b01aa9e.png"
                alt="Saquarius"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-semibold text-white text-sm">Granted</span>
              <span
                style={{ fontSize: '9px', color: '#C9A450', letterSpacing: '0.12em' }}
                className="font-medium uppercase"
              >
                by Saquarius
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-7">
            <Link to="/grants" className={linkClass}>
              Find Grants
            </Link>
            <Link to="/services" className={linkClass}>
              Services
            </Link>
            <Link to="/pricing" className={linkClass}>
              Pricing
            </Link>
            <Link to="/find-a-writer" className={linkClass}>
              Find a Writer
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className="text-sm text-[#C9A450] hover:text-white transition-colors duration-150 font-medium"
              >
                Admin
              </Link>
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {!isPending &&
              (session?.user ? (
                <>
                  <Link
                    to="/dashboard"
                    style={{ backgroundColor: '#C9A450', color: '#1E3A2E' }}
                    className="text-sm font-semibold px-4 py-1.5 rounded-full hover:opacity-90 transition-opacity"
                  >
                    Dashboard →
                  </Link>
                  <Link to="/account/logout?callbackUrl=/" className={linkClass}>
                    Sign Out
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/account/signin?callbackUrl=/dashboard" className={linkClass}>
                    Sign In
                  </Link>
                  <Link
                    to="/account/signup?callbackUrl=/dashboard"
                    style={{ backgroundColor: '#C9A450', color: '#1E3A2E' }}
                    className="text-sm font-semibold px-4 py-1.5 rounded-full hover:opacity-90 transition-opacity"
                  >
                    Get Started
                  </Link>
                </>
              ))}
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-gray-300 hover:text-white p-1"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          style={{ backgroundColor: '#162e23' }}
          className="md:hidden border-t border-[#2D5040] px-4 pb-4 pt-2"
        >
          <div className="flex flex-col">
            {[
              { to: '/grants', label: 'Find Grants' },
              { to: '/services', label: 'Services' },
              { to: '/pricing', label: 'Pricing' },
              { to: '/find-a-writer', label: 'Find a Writer' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-sm text-gray-300 hover:text-white py-3 border-b border-[#2D5040]"
              >
                {item.label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                className="text-sm text-[#C9A450] hover:text-white py-3 border-b border-[#2D5040] font-medium"
              >
                Admin
              </Link>
            )}
            <div className="pt-4 flex flex-col gap-2.5">
              {session?.user ? (
                <>
                  <Link
                    to="/dashboard"
                    style={{ backgroundColor: '#C9A450', color: '#1E3A2E' }}
                    className="text-sm text-center font-semibold py-2.5 rounded-full"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/account/logout?callbackUrl=/"
                    className="text-sm text-center text-gray-300 hover:text-white py-2"
                  >
                    Sign Out
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/account/signin?callbackUrl=/dashboard"
                    className="text-sm text-center text-gray-300 hover:text-white py-2"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/account/signup?callbackUrl=/dashboard"
                    style={{ backgroundColor: '#C9A450', color: '#1E3A2E' }}
                    className="text-sm text-center font-semibold py-2.5 rounded-full"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
